/**
 * Flight Details Thunks
 *
 * Async thunks for flight details operations
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { FlightDetailsContainerData } from '@contracts';
import {
  fetchFlightDetails as fetchFlightDetailsApi,
  FlightDetailsError,
  type FlightDetailsResponse,
} from '@/features/flights/redux/api/flightDetailsApi';
import type { RootState, AppDispatch } from '@/store/store';
import { logger } from '@/shared/utils/logger';
import { getOrCreateSessionId } from '@/shared/utils';
import { getUUID } from '@/shared/utils/appCookies';

// ============================================
// Types
// ============================================

interface FetchFlightDetailsResult {
  flightDetails: FlightDetailsResponse;
  isRoundTrip: boolean;
}

interface FetchFlightDetailsParams {
  contextHashKey: string;
  crId: string;
}

// ============================================
// Type Guards
// ============================================

function isFlightDetailsContainerData(
  data: FlightDetailsResponse,
): data is FlightDetailsContainerData {
  return 'journeyTabs' in data && 'journeys' in data && 'activeJourneyTab' in data;
}

// ============================================
// Thunks
// ============================================

/**
 * Fetch flight details (called from "VIEW DETAILS" button)
 * Handles both one-way (ViewFlightDetails) and round-trip (FlightDetailsContainerData) responses
 */
export const fetchFlightDetailsThunk = createAsyncThunk<
  FetchFlightDetailsResult,
  FetchFlightDetailsParams,
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>('flights/fetchFlightDetails', async ({ contextHashKey, crId }, { rejectWithValue }) => {
  try {
    const sessionId = getOrCreateSessionId();
    const userId = getUUID();

    const flightDetails = await fetchFlightDetailsApi({
      crid: crId,
      rkey: contextHashKey,
      sessionId,
      userId,
    });

    // Determine if it's round-trip or one-way based on response structure
    const isRoundTrip = isFlightDetailsContainerData(flightDetails);

    // Console warn the full response
    logger.warn('[FlightDetailsThunk] Flight Details Response:', {
      flightDetails,
      isRoundTrip,
      hasJourneyTabs: 'journeyTabs' in flightDetails,
      hasTabs: 'tabs' in flightDetails,
    });

    logger.warn('[FlightDetailsThunk] Successfully fetched flight details:', flightDetails);

    return { flightDetails, isRoundTrip };
  } catch (error) {
    logger.error('[FlightDetailsThunk] Failed to fetch flight details:', error);
    if (error instanceof FlightDetailsError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to fetch flight details');
  }
});
